import styled from 'styled-components';
import { Card, BRAND_TINT, BRAND_BORDER, TEXT_PRIMARY, TEXT_MUTED } from './PurchaseConfirmation.styles';

// Styles for the "Join your course WhatsApp group" card on the purchase confirmation page.

// The card starts with a full-width "Next step" bar (like a banner), then the body.
export const WhatsAppCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

export const NextStepBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 1.25rem;
  background: #25493E;
  color: #fff;
  font-size: 13px;
  line-height: 1.2;

  .label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

export const WhatsAppBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
`;

export const WhatsAppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CourseJoinRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  background: ${BRAND_TINT};
  border-radius: 6px;
  padding: 0.75rem;
  color: ${TEXT_PRIMARY};
`;

export const CourseJoinText = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  flex: 1 1 11rem;
  min-height: 40px; /* as tall as the buttons, so the course name sits level with them */
  min-width: 0;
`;

export const CourseJoinName = styled.span`
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const CourseJoinMeta = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${TEXT_MUTED};
`;

// Dark green of the join button (hover goes a shade darker).
const WHATSAPP_GREEN = '#25493E';
const WHATSAPP_GREEN_DARK = '#1B362E';

export const JoinButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 40px;
  padding: 0 1.125rem;
  border-radius: 6px;
  background: ${WHATSAPP_GREEN};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover {
    background: ${WHATSAPP_GREEN_DARK};
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid ${WHATSAPP_GREEN};
    outline-offset: 2px;
  }
`;

// The two buttons side by side: Join (primary) and QR code (outlined).
export const JoinActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 100%;

    & > * {
      flex: 1;
    }
  }
`;

export const QrToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 40px;
  padding: 0 1rem;
  border: 1px solid ${BRAND_BORDER};
  border-radius: 6px;
  background: #FBF8F2;
  color: ${WHATSAPP_GREEN};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #fff;
  }

  &:focus-visible {
    outline: 2px solid ${WHATSAPP_GREEN};
    outline-offset: 2px;
  }
`;

export const QrPanel = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: ${BRAND_TINT};
  border-radius: 8px;
  padding: 1rem 2.75rem 1rem 1rem; /* room at the right for the close button */

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

// "x" in the top right corner of the panel.
export const QrClose = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${TEXT_MUTED};
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: ${TEXT_PRIMARY};
  }

  &:focus-visible {
    outline: 2px solid ${WHATSAPP_GREEN};
    outline-offset: 1px;
  }
`;

export const QrBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 128px;
  height: 128px;
  background: #F3EEE3; /* same as the QR code's own background, so the two blend */
  border: 1px dashed ${BRAND_BORDER};
  border-radius: 8px;
  color: ${TEXT_MUTED};
  font-size: 12px;

  img {
    display: block;
    width: 112px;
    height: 112px;
  }
`;

export const QrText = styled.div`
  min-width: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${TEXT_PRIMARY};

  p {
    margin: 0 0 0.625rem;
  }
`;

export const QrOpenLink = styled.a`
  font-size: 14px;
  font-weight: 700;
  color: ${WHATSAPP_GREEN};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${WHATSAPP_GREEN_DARK};
  }
`;


// ---- "Next steps" card for a new student: 1) set a password, 2) join the WhatsApp group(s).
export const StepsIntro = styled.p`
  margin: 0;
  color: ${TEXT_MUTED};
  font-size: 14px;
  line-height: 1.55;
`;

export const StepBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  & + & {
    border-top: 1px solid ${BRAND_BORDER};
    padding-top: 1.25rem;
  }
`;

export const StepHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${TEXT_PRIMARY};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
`;

export const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${WHATSAPP_GREEN};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
`;

// The step's one-line explanation (the page's muted text size).
export const StepText = styled.p`
  margin: 0;
  color: ${TEXT_MUTED};
  font-size: 14px;
  line-height: 1.55;
`;

// "Set my password": the same dark green button as Join, but a <button> that is only as wide as its label.
export const StepButton = styled(JoinButton).attrs({ as: 'button', type: 'button' })`
  align-self: flex-start;
  border: 0;
  font-family: inherit;
  cursor: pointer;
`;
